/* eslint-disable no-console */
/* eslint-disable max-len */
import {
  addDoc,
  arrayUnion,
  collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where,
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from '../firebase';
import IRecipe from '../interfaces/IRecipe';
import IIngredient from '../interfaces/IIngredient';
import { RHFSelectOption } from '../interfaces/RHFSelectOption';
import IAmount from '../interfaces/IAmount';
import INote from '../interfaces/INote';
import ITag from '../interfaces/ITag';
import IComment, { ICommentReply } from '../interfaces/IComment';
import ICategoryCard from '../interfaces/ICategoryCard';
import ISiteConfig from '../interfaces/ISiteConfig';
import IUser from '../interfaces/IUser';
import IUnit from '../interfaces/IUnit';
import IRecipeBacklog, { CreateBacklogItemData, BacklogStatus, BacklogPriority } from '../interfaces/IRecipeBacklog';

const amountsCollection = collection(db, 'Amounts');
const unitsCollection = collection(db, 'Units');
const commentsCollection = collection(db, 'Comments');
const homeHeroItemsCollection = collection(db, 'HomeHeroItems');
const homeHeroSmallItemsCollection = collection(db, 'HomeHeroSmallItems');
const ingredientNotesCollection = collection(db, 'IngredientNotes');
const ingredientsCollection = collection(db, 'Ingredients');
const mealTypeCategoriesCollection = collection(db, 'MealTypeCategories');
const popularCategoriesCollection = collection(db, 'PopularCategories');
const recipesCollection = collection(db, 'Recipes');
const specialDietCategoriesCollection = collection(db, 'SpecialDietCategories');
const tagsCollection = collection(db, 'Tags');
const recipeBacklogCollection = collection(db, 'RecipeBacklog');

// transforms
export const transformAmountsToOptions = (amounts: IAmount[]): RHFSelectOption[] => amounts.map((amount) => ({
  value: amount.id,
  label: amount.name,
}));
export const transformUnitsToOptions = (units: IUnit[]): RHFSelectOption[] => units.map((unit) => ({
  value: unit.id,
  label: unit.name,
}));
export const transformIngredientsToOptions = (ingredients: IIngredient[]): RHFSelectOption[] => ingredients.map((ingredient) => ({
  value: ingredient.id,
  label: ingredient.name,
}));
export const transformNotesToOptions = (notes: INote[]): RHFSelectOption[] => notes.map((note) => ({
  value: note.id,
  label: note.note,
}));

export const transformTagsToOptions = (tags: ITag[]): RHFSelectOption[] => tags.map((tag) => ({
  value: tag.id,
  label: tag.name,
}));

// export async function MigrateTagIdToName(dryRun = true) {
//   const recipesSnapshot = await getDocs(recipesCollection);

//   let total = 0;

//   const updatePromises = recipesSnapshot.docs.map(async (recipeDoc) => {
//     const recipe = recipeDoc.data();
//     const tagIds = recipe.tags || [];

//     const tagDocs = await Promise.all(
//       tagIds.map((tagId: string) => {
//         const tagRef = doc(db, 'Tags', tagId);
//         return getDoc(tagRef);
//       }),
//     );
//     // const tagDocs = await Promise.all(
//     //   tagIds.map((tagId: string) => getDoc(doc(db, 'tags', tagId))),
//     // );

//     console.log(recipe.title, 'recipetitle');
//     console.log(tagDocs, 'tagdocs');

//     const tagNames = tagDocs
//       .filter((tagDoc) => tagDoc.exists())
//       .map((tagDoc) => tagDoc.data()?.name)
//       .filter(Boolean);

//     if (dryRun) {
//       console.log(`Would update recipe "${recipe.title}" (${recipeDoc.id}) with tags:`, tagNames);
//     } else {
//       await updateDoc(recipeDoc.ref, { tags: tagNames });
//       console.log(`✅ Updated "${recipe.title}" (${recipeDoc.id}) with tags:`, tagNames);
//     }

//     // eslint-disable-next-line no-plusplus
//     total++;
//   });

//   await Promise.all(updatePromises);

//   console.log(`\n✅ ${dryRun ? 'Dry run complete' : 'Migration complete'} for ${total} recipes.`);
// }

export async function getAllHomeHeroItems(): Promise<ICategoryCard[]> {
  const querySnapshot = await getDocs(homeHeroItemsCollection);

  // Use Promise.all to wait for all promises to resolve
  const data = await Promise.all(querySnapshot.docs.map(async (document) => {
    const { id } = document;
    console.log(id, 'id');
    const documentData = document.data();
    const categoryCard: ICategoryCard = {
      id,
      tag: documentData.tag,
      image: documentData.image,
    };

    return categoryCard;
  }));

  return data;
}
export async function getAllHomeHeroSmallItems() {
  const querySnapshot = await getDocs(homeHeroSmallItemsCollection);

  // Use Promise.all to wait for all promises to resolve
  const data = await Promise.all(querySnapshot.docs.map(async (document) => {
    const { id } = document;
    const documentData = document.data();

    const categoryCard: ICategoryCard = {
      id,
      tag: documentData.tag,
      image: documentData.image,
    };

    return categoryCard;
  }));

  return data;
}
export async function getMealTypeCategories() {
  const querySnapshot = await getDocs(mealTypeCategoriesCollection);

  // Use Promise.all to wait for all promises to resolve
  const data = await Promise.all(querySnapshot.docs.map(async (document) => {
    const { id } = document;
    const documentData = document.data();

    const categoryCard: ICategoryCard = {
      id,
      tag: documentData.tag,
      image: documentData.image,
    };

    return categoryCard;
  }));

  // Filter out any null items
  const filteredData = data.filter((item): item is ICategoryCard => item !== null);

  return filteredData;
}
export async function getPopularCategories() {
  const querySnapshot = await getDocs(popularCategoriesCollection);

  // Use Promise.all to wait for all promises to resolve
  const data = await Promise.all(querySnapshot.docs.map(async (document) => {
    const { id } = document;
    const documentData = document.data();
    const categoryCard: ICategoryCard = {
      id,
      tag: documentData.tag,
      image: documentData.image,
    };

    return categoryCard;
  }));

  // Filter out any null items
  const filteredData = data.filter((item): item is ICategoryCard => item !== null);

  return filteredData;
}
export async function getSpecialDietCategories() {
  const querySnapshot = await getDocs(specialDietCategoriesCollection);

  // Use Promise.all to wait for all promises to resolve
  const data = await Promise.all(querySnapshot.docs.map(async (document) => {
    const { id } = document;
    const documentData = document.data();

    const categoryCard: ICategoryCard = {
      id,
      tag: documentData.tag,
      image: documentData.image,
    };

    return categoryCard;
  }));

  // Filter out any null items
  const filteredData = data.filter((item): item is ICategoryCard => item !== null);

  return filteredData;
}

/**
 * Get consolidated site configuration from a single Firestore document.
 * Reduces 5 collection reads to 1 document read.
 * Falls back to individual collection fetches if SiteConfig doesn't exist yet.
 */
export async function getSiteConfig(): Promise<ISiteConfig> {
  const siteConfigRef = doc(db, 'SiteConfig', 'main');
  const siteConfigSnap = await getDoc(siteConfigRef);

  if (siteConfigSnap.exists()) {
    const data = siteConfigSnap.data();
    return {
      homeHeroItems: data.homeHeroItems || [],
      homeHeroSmallItems: data.homeHeroSmallItems || [],
      mealTypeCategories: data.mealTypeCategories || [],
      popularCategories: data.popularCategories || [],
      specialDietCategories: data.specialDietCategories || [],
    };
  }

  // Fallback: fetch from individual collections (for backward compatibility)
  console.warn('SiteConfig document not found, falling back to individual collections. Run migration script to consolidate.');
  const [
    homeHeroItems,
    homeHeroSmallItems,
    mealTypeCategories,
    popularCategories,
    specialDietCategories,
  ] = await Promise.all([
    getAllHomeHeroItems(),
    getAllHomeHeroSmallItems(),
    getMealTypeCategories(),
    getPopularCategories(),
    getSpecialDietCategories(),
  ]);

  return {
    homeHeroItems,
    homeHeroSmallItems,
    mealTypeCategories,
    popularCategories,
    specialDietCategories,
  };
}

export async function getAllUniqueIngredients() {
  const querySnapshot = await getDocs(ingredientsCollection);
  const data = querySnapshot.docs.map((document) => {
    const documentData = document.data();
    const ingredient: IIngredient = {
      id: document.id,
      name: documentData.name,
    };
    return ingredient;
  });
  return data;
}

export async function getAllAmounts() {
  const querySnapshot = await getDocs(amountsCollection);
  const data = querySnapshot.docs.map((document) => {
    const documentData = document.data();
    const amount: IAmount = {
      id: document.id,
      name: documentData.name,
    };
    return amount;
  });
  return data;
}

export async function getAllUnits() {
  const querySnapshot = await getDocs(unitsCollection);
  const data = querySnapshot.docs.map((document) => {
    const documentData = document.data();
    const unit: IUnit = {
      id: document.id,
      name: documentData.name,
    };
    return unit;
  });
  return data;
}

export async function getAllNotes() {
  const querySnapshot = await getDocs(ingredientNotesCollection);
  const data = querySnapshot.docs.map((document) => {
    const documentData = document.data();
    const note: INote = {
      id: document.id,
      note: documentData.note,
    };
    return note;
  });
  return data.sort();
}

export async function getAllTags() {
  const querySnapshot = await getDocs(tagsCollection);
  const data = querySnapshot.docs.map((document) => {
    const documentData = document.data();
    const tag: ITag = {
      id: document.id,
      name: documentData.name,
    };
    return tag;
  });
  return data.sort();
}

export async function getRecipesByTag(tag: string) {
  const queryRecipesDb = query(collection(db, 'Recipes'), where('tags', 'array-contains', tag));
  const querySnapshot = await getDocs(queryRecipesDb);
  return querySnapshot.docs.map((document) => mapRecipeDocument(document.id, document.data()));
}

export async function getRecipesByIngredient(ingredient: string) {
  const queryRecipesDb = query(collection(db, 'Recipes'), where('ingredients', 'array-contains', ingredient));
  const querySnapshot = await getDocs(queryRecipesDb);
  const recipes: IRecipe[] = [];
  querySnapshot.forEach((document) => {
    const documentData = document.data();
    const recipe: IRecipe = {
      id: document.id,
      dateCreated: documentData.date,
      favorites: documentData.favorites,
      instructions: documentData.instructions,
      image: documentData.image,
      ingredients: documentData.ingredients,
      comments: documentData.comments,
      servings: documentData.servings,
      tags: documentData.tags,
      title: documentData.title,
      totalStars: documentData.totalStars,
      commentsWithRatings: documentData.commentsWithRatings,
      averageRating: 0,
    };

    let averageRating = 0;
    if (recipe.totalStars && recipe.commentsWithRatings) {
      averageRating = recipe.totalStars / recipe.commentsWithRatings;
    }
    recipe.averageRating = averageRating;
    recipes.push(recipe);
  });
  return recipes;
}

/**
 * Map Firestore document data to IRecipe interface
 */
function mapRecipeDocument(id: string, data: Record<string, unknown>): IRecipe {
  let averageRating = 0;
  const totalStars = data.totalStars as number | undefined;
  const commentsWithRatings = data.commentsWithRatings as number | undefined;

  if (totalStars && commentsWithRatings) {
    averageRating = totalStars / commentsWithRatings;
  }

  return {
    // Core fields
    id,
    title: data.title as string,
    image: data.image as string,
    ingredients: data.ingredients as IRecipe['ingredients'],
    instructions: data.instructions as IRecipe['instructions'],
    tags: data.tags as string[],
    dateCreated: data.date as string,

    // Time fields
    prepTime: data.prepTime as string | undefined,
    cookTime: data.cookTime as string | undefined,
    totalTime: data.totalTime as string | undefined,

    // Serving info
    servings: data.servings as string,
    yield: data.yield as string | undefined,

    // Ratings & engagement
    averageRating,
    comments: data.comments as string[],
    commentsWithRatings,
    favorites: data.favorites as number,
    totalStars,

    // Recipe metadata
    difficulty: data.difficulty as IRecipe['difficulty'],

    // SEO fields
    slug: data.slug as string | undefined,
    description: data.description as string | undefined,
    cuisine: data.cuisine as IRecipe['cuisine'],
    category: data.category as IRecipe['category'],
    keywords: data.keywords as string[] | undefined,
    author: data.author as string | undefined,

    // Faceted taxonomy fields
    cookingMethods: data.cookingMethods as IRecipe['cookingMethods'],
    dietary: data.dietary as IRecipe['dietary'],
    attributes: data.attributes as IRecipe['attributes'],
  };
}

export async function getAllRecipes() {
  const querySnapshot = await getDocs(recipesCollection);
  return querySnapshot.docs.map((document) => mapRecipeDocument(document.id, document.data()));
}

export async function getAmountById(documentId: string) {
  const docRef = doc(db, 'Amounts', documentId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const amountData = docSnap.data();
    const amount: IAmount = {
      id: documentId,
      name: amountData.name,
    };
    return amount;
  }
  throw new Error('Amount not found');
}

export async function getUnitById(documentId: string) {
  const docRef = doc(db, 'Units', documentId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const unitData = docSnap.data();
    const unit: IUnit = {
      id: documentId,
      name: unitData.name,
    };
    return unit;
  }
  throw new Error('Unit not found');
}

export async function getTagById(documentId: string) {
  const docRef = doc(db, 'Tags', documentId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const tagData = docSnap.data();
    const tag: ITag = {
      id: documentId,
      name: tagData.name,
    };
    return tag;
  }

  throw new Error('Tag not found');
}

export async function getIngredientById(documentId: string) {
  const docRef = doc(db, 'Ingredients', documentId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const ingredientData = docSnap.data();
    const ingredient: IIngredient = {
      id: documentId,
      name: ingredientData.name,
    };
    return ingredient;
  }

  throw new Error('Ingredient not found');
}

export async function getIngredientNotesById(documentId: string): Promise<INote> {
  const docRef = doc(db, 'IngredientNotes', documentId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const ingredientNotesData = docSnap.data();
    const ingredientNotes: INote = {
      id: documentId,
      note: ingredientNotesData.note,
    };
    return ingredientNotes;
  }

  throw new Error('Ingredient notes not found');
}

/**
 * Get a recipe by ID or slug
 * First attempts direct ID lookup, then falls back to slug query
 */
export async function getRecipeById(idOrSlug: string) {
  // Try direct document lookup first (faster for IDs)
  const docRef = doc(db, 'Recipes', idOrSlug);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return mapRecipeDocument(docSnap.id, docSnap.data());
  }

  // If not found by ID, try slug lookup
  const slugQuery = query(recipesCollection, where('slug', '==', idOrSlug));
  const slugSnapshot = await getDocs(slugQuery);

  if (!slugSnapshot.empty) {
    const slugDoc = slugSnapshot.docs[0];
    return mapRecipeDocument(slugDoc.id, slugDoc.data());
  }

  throw new Error('Recipe not found');
}

export async function getFavoriteRecipes(userId: string) {
  const docRef = doc(db, 'Users', userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const user = docSnap.data();
    const recipes = await Promise.all(
      user.favorites.map(async (recipeId: string) => {
        const recipe = await getRecipeById(recipeId);
        return recipe;
      }),
    );
    return recipes;
  }

  return [];
}

export async function getCommentById(documentId: string) {
  const docRef = doc(db, 'Comments', documentId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const commentData = docSnap.data();
    const comment: IComment = {
      id: documentId,
      username: commentData.username,
      content: commentData.content,
      postedAt: commentData.postedAt,
      rating: commentData.rating,
    };

    return comment;
  }

  throw new Error('Comment not found');
}

// Batch fetch comments - uses 'in' query to reduce reads
export async function getCommentsByIds(commentIds: string[]): Promise<IComment[]> {
  if (!commentIds || commentIds.length === 0) return [];

  // Firestore 'in' queries support up to 30 items per query
  const chunks: string[][] = [];
  for (let i = 0; i < commentIds.length; i += 30) {
    chunks.push(commentIds.slice(i, i + 30));
  }

  const results: IComment[] = [];

  // Process each chunk with a single query instead of N individual reads
  // eslint-disable-next-line no-restricted-syntax
  for (const chunk of chunks) {
    const q = query(commentsCollection, where('__name__', 'in', chunk));
    // eslint-disable-next-line no-await-in-loop
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((document) => {
      const data = document.data();
      results.push({
        id: document.id,
        username: data.username,
        content: data.content,
        postedAt: data.postedAt,
        rating: data.rating,
        likes: data.likes || [],
        replies: data.replies || [],
      });
    });
  }

  return results;
}

// Batch fetch recipes by IDs - for favorites page
export async function getRecipesByIds(recipeIds: string[]): Promise<IRecipe[]> {
  if (!recipeIds || recipeIds.length === 0) return [];

  // Firestore 'in' queries support up to 30 items per query
  const chunks: string[][] = [];
  for (let i = 0; i < recipeIds.length; i += 30) {
    chunks.push(recipeIds.slice(i, i + 30));
  }

  const results: IRecipe[] = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const chunk of chunks) {
    const q = query(recipesCollection, where('__name__', 'in', chunk));
    // eslint-disable-next-line no-await-in-loop
    const snapshot = await getDocs(q);
    snapshot.docs.forEach((document) => {
      const documentData = document.data();
      const recipe: IRecipe = {
        id: document.id,
        dateCreated: documentData.date,
        favorites: documentData.favorites,
        instructions: documentData.instructions,
        image: documentData.image,
        ingredients: documentData.ingredients,
        comments: documentData.comments,
        servings: documentData.servings,
        tags: documentData.tags,
        title: documentData.title,
        totalStars: documentData.totalStars,
        commentsWithRatings: documentData.commentsWithRatings,
        averageRating: 0,
      };

      if (recipe.totalStars && recipe.commentsWithRatings) {
        recipe.averageRating = recipe.totalStars / recipe.commentsWithRatings;
      }

      results.push(recipe);
    });
  }

  return results;
}

export async function addRecipe(recipe: IRecipe) {
  console.log(recipe, 'recipetoadd');
  const docRef = await addDoc(recipesCollection, {
    comments: recipe.comments,
    date: recipe.dateCreated,
    image: recipe.image,
    ingredients: recipe.ingredients,
    instructions: recipe.instructions,
    servings: recipe.servings,
    tags: recipe.tags,
    title: recipe.title,
    // SEO fields
    slug: recipe.slug || null,
    description: recipe.description || null,
    cuisine: recipe.cuisine || null,
    category: recipe.category || null,
    keywords: recipe.keywords || null,
    author: recipe.author || 'Evan',
    // Time fields
    prepTime: recipe.prepTime || null,
    cookTime: recipe.cookTime || null,
    totalTime: recipe.totalTime || null,
    yield: recipe.yield || null,
    difficulty: recipe.difficulty || null,
  });
  console.log('docId', docRef.id);
}

/**
 * Update an existing recipe
 * Only updates fields that are provided (partial update)
 */
export async function updateRecipe(recipeId: string, updates: Partial<IRecipe>) {
  const docRef = doc(db, 'Recipes', recipeId);

  // Build update object with only defined fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateData: { [key: string]: any } = {};

  // Core fields
  if (updates.title !== undefined) updateData.title = updates.title;
  if (updates.image !== undefined) updateData.image = updates.image;
  if (updates.ingredients !== undefined) updateData.ingredients = updates.ingredients;
  if (updates.instructions !== undefined) updateData.instructions = updates.instructions;
  if (updates.tags !== undefined) updateData.tags = updates.tags;
  if (updates.servings !== undefined) updateData.servings = updates.servings;

  // Time fields
  if (updates.prepTime !== undefined) updateData.prepTime = updates.prepTime;
  if (updates.cookTime !== undefined) updateData.cookTime = updates.cookTime;
  if (updates.totalTime !== undefined) updateData.totalTime = updates.totalTime;

  // Metadata
  if (updates.difficulty !== undefined) updateData.difficulty = updates.difficulty;
  if (updates.yield !== undefined) updateData.yield = updates.yield;

  // SEO fields
  if (updates.slug !== undefined) updateData.slug = updates.slug;
  if (updates.description !== undefined) updateData.description = updates.description;
  if (updates.cuisine !== undefined) updateData.cuisine = updates.cuisine;
  if (updates.category !== undefined) updateData.category = updates.category;
  if (updates.keywords !== undefined) updateData.keywords = updates.keywords;
  if (updates.author !== undefined) updateData.author = updates.author;

  await updateDoc(docRef, updateData);
  console.log('Recipe updated:', recipeId);
}

export async function createUserInFirestore(userAuth: User, displayName: string) {
  const userRef = doc(db, 'Users', userAuth.uid);
  const docSnap = await getDoc(userRef);

  if (!docSnap.exists()) {
    // If the user document does not exist, create it
    await setDoc(userRef, {
      id: userAuth.uid,
      email: userAuth.email,
      displayName,
      favorites: [], // Set an empty favorites array initially
      role: 'user', // Default role for new users
    });
    console.log('New user document created in Firestore');
  }
}

export async function getUserData(authUser: User) {
  const userRef = doc(db, 'Users', authUser.uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    // Merge Auth user data with Firestore user data
    console.log(userSnap.data(), 'usersnapdata');
    const firebaseuser = userSnap.data();
    const userData: IUser = {
      uid: authUser.uid,
      email: authUser.email,
      favorites: firebaseuser.favorites,
      displayName: firebaseuser.displayName,
      photoURL: authUser.photoURL,
      role: firebaseuser.role || 'user', // Default to 'user' if role not set
    };
    console.log('Merged user data:', userData);
    return userData;
  }
  console.error('User document not found in Firestore');
  return null;
}

export async function addIngredient(ingredient: string) {
  await addDoc(ingredientsCollection, {
    name: ingredient,
  });
}
export async function addAmount(amount: string) {
  await addDoc(amountsCollection, {
    name: amount,
  });
}
export async function addUnit(unit: string) {
  await addDoc(unitsCollection, {
    name: unit,
  });
}
export async function addNote(note: string) {
  await addDoc(ingredientNotesCollection, {
    note,
  });
}

export async function addComment(comment: IComment, recipe: IRecipe): Promise<string> {
  const docRef = await addDoc(commentsCollection, {
    userId: comment.userId,
    username: comment.username,
    content: comment.content,
    postedAt: comment.postedAt,
    rating: comment.rating,
    recipeId: recipe.id,
    likes: [],
    replies: [],
    reportCount: 0,
  });

  console.log('New comment ID:', docRef.id);

  const updatedRecipe = { ...recipe };

  if (comment.rating != null) {
    updatedRecipe.totalStars = (updatedRecipe.totalStars ?? 0) + comment.rating;
    updatedRecipe.commentsWithRatings = (updatedRecipe.commentsWithRatings ?? 0) + 1;

    const recipeRef = doc(db, 'Recipes', recipe.id);
    await updateDoc(recipeRef, {
      comments: arrayUnion(docRef.id),
      totalStars: updatedRecipe.totalStars,
      commentsWithRatings: updatedRecipe.commentsWithRatings,
    });

    return docRef.id;
  }

  const recipeRef = doc(db, 'Recipes', recipe.id);
  await updateDoc(recipeRef, {
    comments: arrayUnion(docRef.id),
  });

  return docRef.id;
}

export async function toggleCommentLike(commentId: string, userId: string): Promise<boolean> {
  const commentRef = doc(db, 'Comments', commentId);
  const commentDoc = await getDoc(commentRef);

  if (!commentDoc.exists()) {
    throw new Error('Comment not found');
  }

  const commentData = commentDoc.data();
  const likes = commentData.likes || [];
  const hasLiked = likes.includes(userId);

  await updateDoc(commentRef, {
    likes: hasLiked
      ? likes.filter((id: string) => id !== userId)
      : [...likes, userId],
  });

  return !hasLiked;
}

export async function addCommentReply(commentId: string, reply: ICommentReply): Promise<void> {
  const commentRef = doc(db, 'Comments', commentId);
  const commentDoc = await getDoc(commentRef);

  if (!commentDoc.exists()) {
    throw new Error('Comment not found');
  }

  await updateDoc(commentRef, {
    replies: arrayUnion(reply),
  });
}

export async function reportComment(commentId: string): Promise<void> {
  const commentRef = doc(db, 'Comments', commentId);
  const commentDoc = await getDoc(commentRef);

  if (!commentDoc.exists()) {
    throw new Error('Comment not found');
  }

  const commentData = commentDoc.data();
  const currentReports = commentData.reportCount || 0;

  await updateDoc(commentRef, {
    reportCount: currentReports + 1,
  });
}

export async function updateUserFavorites(user: IUser, recipeId: string) {
  const userRef = doc(db, 'Users', user.uid); // Get the user's document reference

  console.log(userRef, 'userref');
  let favoriteRecipes = [...user.favorites]; // Copy the user's current favorites array
  console.log(favoriteRecipes, 'fav1');

  if (favoriteRecipes.includes(recipeId)) {
    // If the recipe is already in favorites, remove it
    favoriteRecipes = favoriteRecipes.filter((fav) => fav !== recipeId);
    console.log(favoriteRecipes, 'fav2');
  } else {
    // Otherwise, add the recipeId to the favorites array
    favoriteRecipes.push(recipeId);
    console.log(favoriteRecipes, 'fav3');
  }

  // Update the Firestore document with the new favorites array
  await updateDoc(userRef, {
    favorites: favoriteRecipes,
  });

  console.log('Updated favorites:', favoriteRecipes);
}

// ============================================================================
// RECIPE BACKLOG FUNCTIONS (for n8n workflow)
// ============================================================================

/**
 * Get all backlog items, optionally filtered by status
 */
export async function getBacklogItems(status?: BacklogStatus): Promise<IRecipeBacklog[]> {
  let q;
  if (status) {
    q = query(recipeBacklogCollection, where('status', '==', status));
  } else {
    q = query(recipeBacklogCollection);
  }

  const snapshot = await getDocs(q);
  const items: IRecipeBacklog[] = [];

  snapshot.forEach((docSnap) => {
    const documentData = docSnap.data() as Partial<IRecipeBacklog> & { image?: string };
    const { image, imageUrl, ...rest } = documentData;
    items.push({
      id: docSnap.id,
      ...rest,
      imageUrl: imageUrl || image || '',
    } as IRecipeBacklog);
  });

  // Sort by priority (high first) then by createdAt (oldest first)
  const priorityOrder: Record<BacklogPriority, number> = { high: 0, medium: 1, low: 2 };
  return items.sort((a, b) => {
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}

/**
 * Get a single backlog item by ID
 */
export async function getBacklogItemById(id: string): Promise<IRecipeBacklog | null> {
  const docRef = doc(db, 'RecipeBacklog', id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return null;

  const documentData = docSnap.data() as Partial<IRecipeBacklog> & { image?: string };
  const { image, imageUrl, ...rest } = documentData;
  return {
    id: docSnap.id,
    ...rest,
    imageUrl: imageUrl || image || '',
  } as IRecipeBacklog;
}

/**
 * Add a new item to the backlog
 */
export async function addBacklogItem(data: CreateBacklogItemData): Promise<string> {
  const docRef = await addDoc(recipeBacklogCollection, data);
  return docRef.id;
}

/**
 * Update a backlog item
 */
export async function updateBacklogItem(id: string, data: Partial<IRecipeBacklog>): Promise<void> {
  const docRef = doc(db, 'RecipeBacklog', id);
  await updateDoc(docRef, data);
}

/**
 * Mark a backlog item as processed (called by n8n after creating PendingRecipe)
 */
export async function markBacklogProcessed(
  backlogId: string,
  pendingRecipeId: string,
): Promise<void> {
  const docRef = doc(db, 'RecipeBacklog', backlogId);
  await updateDoc(docRef, {
    status: 'processed',
    processedAt: new Date().toISOString(),
    pendingRecipeId,
  });
}

/**
 * Delete a backlog item
 */
export async function deleteBacklogItem(id: string): Promise<void> {
  const docRef = doc(db, 'RecipeBacklog', id);
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(docRef);
}

/**
 * Get backlog statistics
 */
export async function getBacklogStats(): Promise<{
  pending: number;
  processing: number;
  processed: number;
  skipped: number;
  total: number;
}> {
  const items = await getBacklogItems();
  return {
    pending: items.filter((i) => i.status === 'pending').length,
    processing: items.filter((i) => i.status === 'processing').length,
    processed: items.filter((i) => i.status === 'processed').length,
    skipped: items.filter((i) => i.status === 'skipped').length,
    total: items.length,
  };
}

// =============================================================================
// PENDING RECIPES (n8n generated, awaiting approval)
// =============================================================================

const pendingRecipesCollection = collection(db, 'PendingRecipes');

/**
 * Pending recipe structure from n8n workflow
 */
export interface IPendingRecipe {
  id: string;
  // Recipe data (same shape as IRecipe, minus some computed fields)
  title: string;
  slug: string;
  description: string;
  image?: string;
  imageUrl?: string; // n8n might use this instead of image
  cuisine: string;
  category: string;
  difficulty: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  servings: string;
  yield: string;
  tags: string[];
  keywords: string[];
  cookingMethods: string[];
  dietary: string[];
  attributes: string[];
  ingredients: IRecipe['ingredients'];
  instructions: IRecipe['instructions'];
  author: string;
  notes?: string;
  // Metadata from n8n
  generatedAt: string;
  generatedBy: string;
  aiModel: string;
  backlogId: string;
}

/**
 * Get all pending recipes awaiting approval
 */
export async function getPendingRecipes(): Promise<IPendingRecipe[]> {
  const snapshot = await getDocs(pendingRecipesCollection);
  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as IPendingRecipe[];
}

/**
 * Get a single pending recipe by ID
 */
export async function getPendingRecipe(id: string): Promise<IPendingRecipe | null> {
  const docRef = doc(db, 'PendingRecipes', id);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as IPendingRecipe;
}

/**
 * Approve a pending recipe: move to Recipes collection and mark backlog as processed
 */
export async function approvePendingRecipe(pendingId: string): Promise<string> {
  const { deleteDoc } = await import('firebase/firestore');

  console.log('Approving pending recipe:', pendingId);

  // Get the pending recipe
  const pending = await getPendingRecipe(pendingId);
  console.log('Pending recipe data:', pending);
  if (!pending) throw new Error('Pending recipe not found');

  // Build the recipe document (without pending-specific fields)
  // Handle missing fields with sensible defaults
  const imageUrl = pending.imageUrl || pending.image || '';
  const recipeData: Omit<IRecipe, 'id'> = {
    title: pending.title || 'Untitled Recipe',
    slug: pending.slug || pending.title?.toLowerCase().replace(/\s+/g, '-') || 'untitled',
    description: pending.description || '',
    image: imageUrl,
    cuisine: (pending.cuisine || undefined) as IRecipe['cuisine'],
    category: (pending.category || undefined) as IRecipe['category'],
    difficulty: (pending.difficulty || 'easy') as IRecipe['difficulty'],
    prepTime: pending.prepTime || '',
    cookTime: pending.cookTime || '',
    totalTime: pending.totalTime || '',
    servings: pending.servings || '',
    yield: pending.yield || '',
    tags: pending.tags || [],
    keywords: pending.keywords || [],
    cookingMethods: (pending.cookingMethods || []) as IRecipe['cookingMethods'],
    dietary: (pending.dietary || []) as IRecipe['dietary'],
    attributes: (pending.attributes || []) as IRecipe['attributes'],
    ingredients: pending.ingredients || [],
    instructions: pending.instructions || [],
    author: pending.author || 'Evan',
    // Initialize computed fields
    favorites: 0,
    averageRating: 0,
    comments: [],
    dateCreated: pending.generatedAt || new Date().toISOString(),
  };

  // Create in Recipes collection
  console.log('Creating recipe with data:', recipeData);
  const recipeRef = await addDoc(recipesCollection, recipeData);
  console.log('Recipe created with ID:', recipeRef.id);

  // Mark backlog item as processed
  if (pending.backlogId) {
    try {
      await updateBacklogItem(pending.backlogId, {
        status: 'processed',
        processedAt: new Date().toISOString(),
        pendingRecipeId: recipeRef.id,
      });
    } catch (error) {
      console.warn('Could not update backlog item:', error);
    }
  }

  // Delete from PendingRecipes
  await deleteDoc(doc(db, 'PendingRecipes', pendingId));

  return recipeRef.id;
}

/**
 * Reject a pending recipe: delete it and reset backlog to pending
 */
export async function rejectPendingRecipe(pendingId: string): Promise<void> {
  const { deleteDoc } = await import('firebase/firestore');

  // Get the pending recipe to find backlog reference
  const pending = await getPendingRecipe(pendingId);
  if (!pending) throw new Error('Pending recipe not found');

  // Reset backlog item to pending (so it can be re-processed)
  if (pending.backlogId) {
    try {
      await updateBacklogItem(pending.backlogId, {
        status: 'pending',
      });
    } catch (error) {
      console.warn('Could not reset backlog item:', error);
    }
  }

  // Delete from PendingRecipes
  await deleteDoc(doc(db, 'PendingRecipes', pendingId));
}
